import type { ResliceLapsResult } from './circuitLapReslice';
import { buildCircuitLapReslice } from './circuitLapReslice';
import type { CircuitMapOverlay } from './circuitMapOverlay';
import type { MyChronSessionExport, TrackMapPoint } from './telemetrySession';

const MAX_ENTRIES = 48;

type LapMapPointsKey = string;
type ResliceKey = string;

const lapMapPointsCache = new Map<LapMapPointsKey, TrackMapPoint[]>();
const resliceCache = new Map<ResliceKey, ResliceLapsResult | null>();

function touch<K, V>(cache: Map<K, V>, key: K, value: V) {
  cache.delete(key);
  cache.set(key, value);
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) {
      cache.delete(oldest);
    }
  }
}

export function lapMapPointsCacheKey(input: {
  sessionId: string | null;
  lapNumber: number;
  lapTimingMode: string;
  resliceKey: string;
}) {
  return `${input.sessionId ?? 'none'}:${input.lapTimingMode}:${input.lapNumber}:${input.resliceKey}`;
}

export function circuitResliceCacheKey(sessionId: string, overlay: CircuitMapOverlay) {
  const start = overlay.startFinishLine;
  return `${sessionId}:${overlay.contributionId ?? 'none'}:${overlay.trackId ?? 'none'}:${start?.pointA.lat ?? 0}:${start?.pointA.lon ?? 0}:${start?.pointB.lat ?? 0}:${start?.pointB.lon ?? 0}`;
}

export function getCachedLapMapPoints(key: LapMapPointsKey) {
  return lapMapPointsCache.get(key);
}

export function setCachedLapMapPoints(key: LapMapPointsKey, points: TrackMapPoint[]) {
  touch(lapMapPointsCache, key, points);
}

export function getCachedCircuitLapReslice(
  sessionId: string,
  overlay: CircuitMapOverlay,
  session: MyChronSessionExport,
) {
  const key = circuitResliceCacheKey(sessionId, overlay);
  if (resliceCache.has(key)) {
    return resliceCache.get(key)!;
  }

  const result = buildCircuitLapReslice(session, overlay);
  touch(resliceCache, key, result);
  return result;
}

export function clearSessionGeometryCache() {
  lapMapPointsCache.clear();
  resliceCache.clear();
}
